import type { MonoTypeOperatorFunction } from 'rxjs'
import { retry, throwError, timer } from 'rxjs'

const statusOf = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

/* axios reports a request that got no answer with no status at all, where the RxJS layer says 0 */
const unanswered = (error: unknown): boolean => {
  const axiosError = error as { isAxiosError?: unknown; response?: unknown } | null
  return axiosError?.isAxiosError === true && !axiosError.response
}

/** A failure worth repeating unchanged: the server erred (5xx), or the request never got an answer. */
export const isTransient = (error: unknown): boolean => {
  const status = statusOf(error)
  return status === 0 || (status !== undefined && status >= 500) || unanswered(error)
}

export const isVersionConflict = (error: unknown): boolean => statusOf(error) === 409 || statusOf(error) === 412

export type RetryOptions = {
  /** Also repeat on a version conflict; only for a chain that reads the current version itself. */
  conflicts?: boolean
  delaysMs?: readonly number[]
}

export const RETRY_DELAYS_MS = [1000, 3000, 8000] as const

/**
 * Repeats an idempotent request after transient failures, with growing pauses. Never for a request
 * that creates something: a 5xx can arrive after the server made it, and a repeat would make a second.
 */
export const retryTransient = <T>({ conflicts = false, delaysMs = RETRY_DELAYS_MS }: RetryOptions = {}): MonoTypeOperatorFunction<T> =>
  retry({
    count: delaysMs.length,
    delay: (error: unknown, attempt) =>
      isTransient(error) || (conflicts && isVersionConflict(error)) ? timer(delaysMs[attempt - 1]) : throwError(() => error)
  })
